<template>
    <div class="layout">
        <wiselli-sdk
            v-if="wiselliAbTestVariant === 'WiselliControl'"
            app-id="0c4555ba-81f6-4f81-9c6f-d12fbb202914/44eeae62-c44b-4338-b0b1-acf7b3869557"
            track-data-layer="true"
            global-split="WiselliControl"
        ></wiselli-sdk>
        <wiselli-sdk
            v-else
            app-id="0c4555ba-81f6-4f81-9c6f-d12fbb202914/44eeae62-c44b-4338-b0b1-acf7b3869557"
            track-data-layer="true"
            global-split="WiselliTarget"
        ></wiselli-sdk>

        <NuxtLoadingIndicator color="#146154" />
        <Header
            v-if="!isWebviewInApp"
            :is-desktop="$device.isDesktop"
            :is-tablet="$device.isTablet"
            :is-mobile="$device.isMobile"
            :is-minimal-header="isMinimalHeader"
            :items="menuItems"
            :basket="basket"
            :cart-box-banner="cartBoxBanner"
            :pinned-categories="pinnedCategories"
            :menu-banners="menuBanners"
            :menu-brand-banners="menuBrandBanners"
            :sale-banners="saleBanners"
            :is-user-authenticated="isUserAuthenticated"
            :is-user-premium="isUserPremium"
            :username="username"
            :basic-url="basicUrl"
            :lang-default="locale"
            :favorite-count="favoriteCount"
            :is-pwa="true"
            :menu-item-products="isNotRo ? menuItemProducts : []"
            :is-chat-enabled="isChatEnabled"
            :xmas-category-id="xmasCategoryId"
            :headline-bar-data="headlineBar"
            :menu-highlight-category="menuHighlightCategory"
            :last-visited-product-ids="lastVisitedProductIds"
            :product-amounts="productAmounts"
            class="topHeader"
            :progress-bars="progressBars"
            :dictionary="headerDictionary"
            :is-xmas-season="isXmasSeason"
            :fastest-possible-delivery-date="fastestPossibleDeliveryDate"
            :customer-care-status="customerCareStatus"
            :pilulka-pro-enabled="$config.public.pilulkaProEnabled"
            @menu-is-visible="loadMenuFromApi"
            @mouseover="headerMouseOver"
            @add-product-to-basket="addProductToBasket"
            @redirect-to-url="redirectToUrl"
            @autocomplete-results="autocompleteResults"
            @autocomplete-click-result="autocompleteClickResult"
            @fastest-possible-delivery-date-clicked="fastestPossibleDeliveryDateClicked"
            @logged="logged"
        >
            <template #product="{ product }">
                <ElementsProductCard
                    :product="product"
                    :show-pre-basket="false"
                    visual-mode="vertical"
                    @product-redirected-to-url="redirectToUrl"
                    @product-added-to-basket="(productUrl: string) => {}"
                />
            </template>
        </Header>

        <ElementsAppBanner v-if="!$device.isDesktop" />

        <div
            id="pageWrap"
            class="pageWrap"
            :class="{
                'pageWrap--with-xmas-banner': isXmasSeason && headlineBar,
                'pageWrap--with-headline-bar': headlineBar || isXmasSeason,
                'pageWrap--is-webview-in-app': isWebviewInApp,
                'pageWrap--is-minimal-header': isMinimalHeader,
            }"
        >
            <main class="d-flex m-auto w-100 justify-content-center container-page">
                <slot />
            </main>

            <Footer
                v-if="!isWebviewInApp && !isMinimalHeader"
                id="topFooter"
                :lang="locale"
                :structured-data="structuredData"
                class="topFooter"
                :dictionary="$tm('footerDictionary')"
                :list-pay-methods="$tm('listPayMethods')"
                :is-pwa="true"
                :android-url="mobileAppLink"
                :ios-url="mobileAppLink"
                @open-registration-pop-up="openRegistrationPopUp"
                @newsletter-registration-form-submitted="newsletterRegistrationFormSubmitted"
                @translate-web="translateWeb"
            />
        </div>
        <div id="overlay"></div>
        <Cookiebar :show-cookie="!isWebviewInApp" />
        <ModalDelivery />
        <MigrateToMobileAppModal />
        <ModalPriceInPharmacy />
        <DelayHydration>
            <ObjectsPreBasket />
        </DelayHydration>

        <!-- Preload CSS and JS for product and category page -->
        <div class="d-none">
            <nuxt-link to="/product-page" rel="nofollow"></nuxt-link>
            <nuxt-link to="/category-page" rel="nofollow"></nuxt-link>
        </div>

        <ElementsRegistrationModal />
        <ElementsPopUpModal v-if="urlPopup" :pop-up="urlPopup" :show-immediately="true" />
        <ElementsCart2PremiumModal />

        <ModalTranslation />

        <Head>
            <Meta name="apple-itunes-app" hid="apple-itunes-app" :content="`app-id=${appId}, app-argument=${mobileAppLink}`" />
            <Meta hid="al:ios:url" property="al:ios:url" :content="mobileAppLink" />
            <Meta hid="al:ios:app_name" property="al:ios:app_name" content="Pilulka" />
            <Meta hid="al:ios:app_store_id" property="al:ios:app_store_id" :content="appId" />
            <Meta name="google-site-verification" content="HbhKnXmMPc4IjgUf8CnRuVCWkqSpMwMGRhp42htdrZ8" />
        </Head>
    </div>
</template>

<script lang="ts">
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { NuxtApp, NuxtSSRContext, useNuxtApp } from '#app'
import { MetaObject } from '@nuxt/schema'
import type { RuntimeConfig } from 'nuxt/schema'
// @ts-ignore
// import Header from '../components/myNodeModules/eshop-pwa-header/src/components/Header.vue'
import Header from '../node_modules/eshop-pwa-header/src/components/Header.vue'
// @ts-ignore
// import Footer from '~/components/myNodeModules/eshop-pwa-footer/src/components/TheFooter.vue'
import Footer from '../node_modules/eshop-pwa-footer/src/components/TheFooter.vue'
import DataLayer from '~/extensions/DataLayer'
import Cookiebar from '~/components/objects/Cookiebar.vue'
import { useLayoutStore } from '~/stores/layout'
import { addProductToBasket } from '~/utils/addProductToBasket'
import { useBasketStore } from '~/stores/basket'
import { useAuthStore } from '~/stores/auth'
import { usePreBasketStore } from '~/stores/preBasket'
import MigrateToMobileAppModal from '~/components/modal/MigrateToMobileAppModal.vue'
import { GtmItemListNameEnum } from '~/models/DataLayer/GtmItemListNameEnum'
import { GtmEcommerceEventTypeEnum } from '~/models/DataLayer/GtmEcommerceEventTypeEnum'
import GtmEcommerceEvent from '~/models/DataLayer/GtmEcommerceEvent'
import { transformFullProductToGtmProduct } from '~/utils/transformFullProductToGtmProduct'
import LuigisBoxEventApi from '~/extensions/LuigisBoxEventApi'
import GtmProduct from '~/models/Product/GtmProduct'
import { useMatomoSetCustomUrl } from '~/composables/useMatomo'
import Product from '~/models/Product/Product'
import LuigisBoxItem from '~/models/LuigisBox/LuigisBoxItem'
import { useCookie } from '#imports'
import { useRegistrationModalStore } from '~/stores/registrationModal'
import { useAbTestStore } from '~/stores/abTest'

export default defineNuxtComponent({
    components: {
        MigrateToMobileAppModal,
        Footer,
        Header,
        Cookiebar,
    },
    transition: 'page',

    data() {
        return {
            lastScrollTop: 0,
            menuFullyLoaded: false,
            mobileAppLink: useMobileAppLink(),
            customerCareStatus: getCustomerCareStatus(),
        }
    },

    watch: {
        $route() {
            this.mobileAppLink = useMobileAppLink(this.$route.fullPath)
        },
    },

    mounted() {
        const preBasketStore = usePreBasketStore()

        this.mobileAppLink = ''
        setTimeout(() => {
            this.mobileAppLink = useMobileAppLink(this.$route.fullPath, useCjEventCookie().value)
        }, 1000)

        let listPosition = -1
        let bundleProducts: GtmProduct[] = []
        let productIdReferrer: number | null = null
        // @ts-ignore
        window.addToBasket = async function (productId, amount, listName = '') {
            const result = await useNuxtApp().$repository.getProductDetailData(productId, useUidCookie().value)
            let skipPushToDataLayer = false
            if (listName.includes('dynamic_bundle')) {
                skipPushToDataLayer = true
                listName = GtmItemListNameEnum.ProductDetailDynamicBundle
                bundleProducts.push(transformFullProductToGtmProduct(result.product))
                if (productIdReferrer === null) {
                    productIdReferrer = result.product.id
                }
                listPosition++
            } else if (listName === 'prebasket') {
                listPosition = 0
                productIdReferrer = preBasketStore.productIdReferrer
            } else if (listName === 'detail') {
                listName = GtmItemListNameEnum.ProductDetailRecommendationProducts
            } else {
                listPosition = 0
            }

            addProductToBasket({
                fullProduct: result.product,
                productId: result.product.id,
                amount,
                showPreBasket: false,
                itemListName: listName,
                listPosition,
                skipPushToDataLayer,
                productIdReferrer,
            })

            if (skipPushToDataLayer && listPosition > 0) {
                const ecommerceEvent = {
                    type: GtmEcommerceEventTypeEnum.AddToCart,
                    products: bundleProducts,
                    itemListName: 'dynamic_bundles',
                    productAmount: 1,
                    productIdReferrer,
                } as GtmEcommerceEvent
                DataLayer.pushEcommerceEventToDataLayer(ecommerceEvent)

                productIdReferrer = null
                bundleProducts = []
            }

            if (listPosition >= 1) {
                listPosition = -1
            }
        }

        const handleMessage = async ({ data }: any) => {
            const { type, value } = data

            switch (type) {
                case '@outfindo/guide/analytics': {
                    if (data?.payload.event === 'guide_started') {
                        DataLayer.push({
                            event: 'outfindo_form_start',
                        })
                    }

                    break
                }
                case 'ADD_TO_BASKET': {
                    const { id: productId } = value

                    const result = await useNuxtApp().$repository.getProductDetailData(productId, useUidCookie().value)
                    const amount = (useBasketStore().productAmounts[productId] || 0) + 1

                    addProductToBasket({
                        fullProduct: result.product,
                        productId: result.product.id,
                        amount,
                        showPreBasket: false,
                    })

                    break
                }
                default: {
                    break
                }
            }
        }
        window.addEventListener('message', (event) => handleMessage(event), false)

        if (this.$device.isAndroid) {
            document.body.classList.add('isAndroid')
        }
        if (this.$device.isIos) {
            document.body.classList.add('isIos')
        }
        if (this.$device.isDesktop) {
            document.body.classList.add('isDesktop')
        }
        if (this.$device.isTablet) {
            document.body.classList.add('isTablet')
        }
        if (this.$device.isMobile) {
            document.body.classList.add('isMobile')
        }

        try {
            const authStore = useAuthStore()
            this.$repository.saveCjEventId(useRoute().query, authStore.accessToken)
        } catch {}

        useMatomoSetCustomUrl()

        if (this.isPopUpRegistrationInQuery) {
            useRegistrationModalStore().scrollBehaviorEnabled = true
            useRegistrationModalStore().footerModal = false
        } else {
            useRegistrationModalStore().footerModal = true
        }
    },

    computed: {
        urlPopup() {
            const route = useRoute()

            return (
                useLayoutStore().popupsByUrl.find((popup) => popup.urlShouldContain && route.fullPath.includes(popup.urlShouldContain)) ||
                null
            )
        },
        wiselliAbTestVariant() {
            return useAbTestStore().getVariant('WiselliAbTest') === 1 ? 'WiselliTarget' : 'WiselliControl'
        },
        headerDictionary() {
            const dictionary = Object.assign(
                {},
                this.$tm('header'),
                this.$tm('basic'),
                this.$tm('footerDictionary.customerSupport'),
                this.$tm('productDetail'),
                this.$tm('search')
            )

            dictionary.siteHeader.sale.title = useLayoutStore().actionsAndSalesCategoryName || dictionary.siteHeader.sale.title

            return dictionary
        },
        productAmounts() {
            const basketStore = useBasketStore()
            return basketStore.productAmounts
        },

        fastestPossibleDeliveryDate() {
            return useBasketStore().fastestPossibleDeliveryDate
        },

        isWebviewInApp() {
            return this.$route.query.webview ?? false
        },

        isChatEnabled() {
            return this.$config.public.isChatEnabled
        },

        xmasCategoryId() {
            return parseInt(this.$config.public.xmasCategoryId)
        },

        menuHighlightCategory() {
            return this.$config.public.menuHighlightCategory || null
        },

        basicUrl() {
            return useRuntimeConfig().public.apiUrl
        },

        appId() {
            return useRuntimeConfig().public.appId
        },

        isUserAuthenticated() {
            return this.authUser !== null
        },

        isUserPremium() {
            return useBasketStore().authUser?.isPremium || false
        },

        authUser() {
            const basketStore = useBasketStore()

            return basketStore.authUser
        },

        username() {
            const basketStore = useBasketStore()

            if (!this.isUserAuthenticated || !basketStore.authUser) {
                return this.$t('siteHeader.login.title')
            }

            if (basketStore.authUser.firstName === '') {
                return basketStore.authUser.email
            } else {
                return basketStore.authUser.firstName
            }
        },

        menuItems() {
            const layoutStore = useLayoutStore()

            return layoutStore.menuItems
        },

        headlineBar() {
            if (this.isMinimalHeader) {
                return null
            }

            const layoutStore = useLayoutStore()
            return layoutStore.headlineBar
        },

        menuItemProducts() {
            const layoutStore = useLayoutStore()

            return layoutStore.menuItemProducts
        },

        cartBoxBanner() {
            const layoutStore = useLayoutStore()

            return layoutStore.cartBoxBanner || {}
        },

        locale() {
            return useRuntimeConfig().public.locale || 'cz'
        },

        structuredData() {
            return {
                rating: 4.8,
            }
            // const layoutStore = useLayoutStore()
            //
            // return layoutStore.structuredData
        },

        basket() {
            const basketStore = useBasketStore()

            return basketStore.basket
        },

        pinnedCategories() {
            const layoutStore = useLayoutStore()

            return layoutStore.pinnedCategories || []
        },

        menuBanners() {
            const layoutStore = useLayoutStore()

            return layoutStore.menuBanners || []
        },

        menuBrandBanners() {
            const layoutStore = useLayoutStore()

            return (layoutStore.menuBrandBanners || []).slice(0, 9)
        },

        progressBars() {
            const basketStore = useBasketStore()

            return basketStore.progressBars || []
        },

        saleBanners() {
            const layoutStore = useLayoutStore()

            return layoutStore.saleBanners || []
        },

        favoriteCount() {
            const basketStore = useBasketStore()

            return basketStore.favoriteCount || 0
        },

        freeDelivery() {
            return 0
        },

        lastVisitedProductIds() {
            const idProducts = useCookie('lastViewProductsIds').value
            if (idProducts && idProducts.length > 2) {
                return idProducts
            }

            return []
        },

        isNotRo() {
            return useI18n().locale.value !== 'ro'
        },

        isMinimalHeader() {
            return ['cart.step_2', 'cart.step_3'].includes(this.$route.matched[0]?.props.default.componentName)
        },

        isXmasSeason() {
            // IT-835 - xmas header disabled
            return false
            // const date = new Date()
            // const currentDate = date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate()
            // return currentDate < this.$config.public.xmasDelivery
        },

        isPopUpRegistrationInQuery(): boolean {
            return this.$config.public.popUpRegistration && Object.keys(this.$route.query).includes('pop-up-registration')
        },
    },

    methods: {
        async logged(data: any) {
            await useAuthStore().initFromLogin(data.token)
            useUidCookie().value = data.uid
            await useBasketStore().fetchData(true, data.uid)
        },

        translateWeb(lang: string) {
            if (lang === this.locale) {
                useNuxtApp().$bus.$emit('keepOriginalTranslation')

                return
            }

            useNuxtApp().$bus.$emit('translateWeb', lang)
        },

        openRegistrationPopUp() {
            setTimeout(() => {
                useNuxtApp().$bus.$emit('showRegistrationModal', {})
            }, 200)
        },

        async newsletterRegistrationFormSubmitted(email: string) {
            const response = await useNuxtApp().$repository.validateAndRegisterUser(email, true, false)

            if (!response) {
                return
            }

            if (response.isRegistered) {
                alert(this.$t('popUp.registrationComplete'))

                return
            }

            alert(response.errors.email)
        },

        autocompleteResults(queryString: string, items: LuigisBoxItem[]) {
            if (queryString.trim() !== '') {
                LuigisBoxEventApi.sendAutocompleteEvent(queryString, items)
            }
        },

        autocompleteClickResult(clickType: string, productUrl: string) {
            if (clickType === 'conversion') {
                LuigisBoxEventApi.sendClickEvent('buy', productUrl)
            } else {
                LuigisBoxEventApi.sendClickEvent('click', productUrl)
            }
        },

        addProductToBasket({
            product,
            amount,
            showPreBasket,
            itemListName,
        }: {
            product: Product
            amount: number
            showPreBasket: boolean
            itemListName: string
        }) {
            addProductToBasket({
                product,
                productId: product.id,
                amount,
                showPreBasket,
                itemListName,
            })
        },

        async redirectToUrl(event: Event) {
            const href = (event.currentTarget as HTMLElement).getAttribute('href') || ''
            const external = href.startsWith('http')

            await navigateTo(href, { external })
        },

        headerMouseOver() {
            if (this.$device.isDesktop) {
                this.loadMenuFromApi()
            }
        },

        loadMenuFromApi() {
            setTimeout(async () => {
                if (!this.menuFullyLoaded) {
                    this.menuFullyLoaded = true
                    const response = await this.$repository.getPwaMenu()
                    const layoutStore = useLayoutStore()
                    await layoutStore.setMenuBanners(response.menuBanners)
                    await layoutStore.setMenuBrandBanners(response.menuBrandBanners)
                    await layoutStore.setMenuItems(response.menuItems)
                    await layoutStore.setMenuItemProducts(response.menuItemProducts)
                }
            }, 0)
        },

        fastestPossibleDeliveryDateClicked() {
            setTimeout(() => {
                useNuxtApp().$bus.$emit('showDeliveryModal', null)
            }, 0)
        },
    },

    head(nuxtApp: NuxtApp): MetaObject {
        let config: Pick<RuntimeConfig, 'public' | 'app'>
        if (process.client) {
            config = nuxtApp.payload.config as Pick<RuntimeConfig, 'public' | 'app'>
        } else {
            config = (nuxtApp.ssrContext as NuxtSSRContext).config as Pick<RuntimeConfig, 'public' | 'app'>
        }
        const baseUrl = config.public.apiUrl + nuxtApp._route.fullPath
        const layoutStore = useLayoutStore()

        const script: { src: string; async?: true; defer?: true; type?: string; body?: boolean }[] = []
        // script.push({
        //     src: 'https://sdk.wiselli.com/wiselli.min.js',
        //     async: true,
        //     defer: true,
        // })

        script.push({
            src: 'https://embed.outfindo.com/guide/embed.js',
            async: true,
            body: true,
        })

        if (layoutStore.zoeEnabled && config.public.zoeScript && !nuxtApp._route.query.nozoe) {
            script.push({
                src: config.public.zoeScript,
                async: true,
            })
        }
        if (config.public.luigisboxScript && !nuxtApp._route.query.noluigisbox) {
            script.push({
                src: config.public.luigisboxScript,
                async: true,
            })
        }

        if (config.public.isChatBotEnabled) {
            script.push({
                type: 'text/javascript',
                src: '/nuxt-assets/chatbot.js',
            })
        }

        if (config.public.translateScript) {
            script.push({
                src: config.public.translateScript,
                async: true,
            })
        }

        return {
            bodyAttrs: {
                'data-lang': config.public.bodyLang,
                'data-full-path': baseUrl,
            },
            htmlAttrs: {
                lang: config.public.htmlLang,
            },
            script,
        }
    },
})
</script>

<style lang="scss" scoped>
@media (max-width: 768px) {
    .layout {
        overflow-x: hidden;
    }
}

.pageWrap {
    font-weight: 400;
    padding-top: $headerHeightMobile;

    @include screen-md {
        padding-top: $headerHeightDesktop;
    }

    &.pageWrap--is-minimal-header {
        padding-top: $minimalHeaderHeightMobile;

        @include screen-md {
            padding-top: $minimalHeaderHeightDesktop;
        }
    }

    &--with-headline-bar {
        padding-top: $headerHeightMobile + $headlineBarHeightMobile;

        @include screen-md {
            padding-top: $headerHeightDesktop + $headlineBarHeightDesktop;
        }
    }

    &--with-xmas-banner {
        padding-top: 188px;
    }

    &.pageWrap--is-webview-in-app {
        padding-top: 0;
    }
}

//body.isAndroid .pageWrap {
//    padding-top: 143px;
//
//    &--with-headline-bar {
//        padding-top: 184px;
//    }
//
//    &--with-xmas-banner {
//        padding-top: 218px;
//    }
//}
</style>

<style lang="scss">
.amio_chat_wrapper_closed {
    z-index: 0 !important;
}
</style>
